import React, {useEffect, useRef, useState} from "react";
/** @jsxImportSource @emotion/react */
import {css} from "@emotion/react";
import styled from "@emotion/styled";
import {useTheme} from "@emotion/react";
import Player from "./Player";
import {useMobileDetect, useMountEffect} from "../../../utils/Hooks";
import {useModelContext} from "../../../context/ModelContextProvider";
import {
    BROADCAST_ON_SUBJECT_SELECT,
    getImageUrl,
    useApplicationContext,
} from "../../../context/ApplicationContextProvider";
import SubjectDescription from "./SubjectDescription";
import CircleLoading from "../../_common/component/CircleLoading";
import Log from "../../../common/Log";
import {useNavigate} from "react-router-dom";
import {useDialogContext} from "../../../context/DialogContextProvider";
import {lang} from "../../../lang";
import {SECRET_NOTE} from "../../_presentational/buttons";

/*
    props
    course : {} = 현재 url로 전달된 course_id의 course
    subject_set : [{}, {}, ...] = 현재 course의 모든 subject 배열
 */
const SubjectViewer = (props) => {
    const {broadcast} = useApplicationContext();
    const {alert} = useDialogContext();
    const {isSuccess, isError, course_model} = useModelContext();
    const navigate = useNavigate();
    const {is_mobile} = useMobileDetect();

    // state
    const [active_subject, setActiveSubject] = useState({});
    const [is_loading, setIsLoading] = useState(false);

    // ref
    const is_user_disabled = useRef(false);

    // css style
    const {css, view} = useComponentStyle(is_mobile);

    useMountEffect(() => {
        updateUi();
    });

    const updateUi = async () => {
        setIsLoading(true);

        let un_viewed_subject = props.subject_set.find((subject) => !subject.is_viewed);
        if (!un_viewed_subject) {
            un_viewed_subject = props.subject_set[0];
        }

        setActiveSubject(await getSubjectDetail(un_viewed_subject));

        setIsLoading(false);
    };

    useEffect(() => {
        const unlisten = broadcast.listen([BROADCAST_ON_SUBJECT_SELECT], async ({value}) => {
            getSubjectDetail(value).then((subject) => setActiveSubject(subject));

            !is_mobile && window.scrollTo(0, 0);
        });

        return () => {
            broadcast.unlisten(unlisten);
        };
    }, [broadcast, is_mobile, active_subject]);

    const getSubjectDetail = async (subject) => {
        const subject_detail_res = await course_model.getSubjectDetail(props.course.id, subject.id);

        if (isSuccess(subject_detail_res)) {
            return subject_detail_res.data.subject;
        } else if (isError(subject_detail_res)) {
            Log.private("getSubjectDetailViewer 에러", subject_detail_res);
            await alert(lang.request.request_error_course_expired);
            navigate("/");
        }
    };

    return (
        <>
            {!is_mobile && (
                <view.TitleWrap>
                    <span>{props.course.title}</span>
                    <a href={props.course.document_url}>
                        <SECRET_NOTE />
                    </a>
                </view.TitleWrap>
            )}
            <div css={css.viewer_wrap_css}>
                <Player
                    is_user_disabled={is_user_disabled}
                    active_subject={active_subject}
                    subject_set={props.subject_set}
                />
            </div>
            {is_loading ? (
                <div
                    style={{
                        marginTop: "1.5em",
                        textAlign: "center",
                    }}>
                    <CircleLoading size={"sm"} />
                </div>
            ) : (
                <>
                    <view.SubjectTitleWrap>
                        <div className={"subject-title"}>
                            {active_subject.index_name}. {active_subject.title}
                        </div>
                        <img
                            src={getImageUrl("image/common/ic_help_x3.png")}
                            alt="영상 문제 해결"
                            onClick={() => {
                                navigate("/service/info/viewer");
                            }}
                        />
                    </view.SubjectTitleWrap>
                    <SubjectDescription content={active_subject && active_subject.description} />
                </>
            )}
            {is_mobile && (
                <view.MobileSecretNoteWrap>
                    <a href={props.course.document_url}>
                        <span>비법노트 다운받기</span>
                        <div className="download-icon" />
                    </a>
                </view.MobileSecretNoteWrap>
            )}
        </>
    );
};

export default SubjectViewer;

export const useComponentStyle = (is_mobile) => {
    const theme = useTheme();
    return {
        css: {
            viewer_wrap_css: css({
                aspectRatio: "16/9",
                boxSizing: "border-box",
                backgroundColor: theme.colors.black,
                position: "relative",
                marginTop: is_mobile && "4em",
            }),
        },
        view: {
            TitleWrap: styled.div({
                width: "100%",
                height: "3em",
                margin: ".7em 0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                "& > span": {
                    fontSize: theme.font_size.f20,
                    fontWeight: "700",
                },
            }),

            SubjectTitleWrap: styled.div({
                width: is_mobile ? "calc(100% - 2em)" : "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                margin: is_mobile ? "1.2em 1em" : "1.2em 0",
                transition: "all .2s",
                "@media (max-width: 320px)": {
                    margin: "1.2em 0",
                    width: "100%",
                },
                "& > .subject-title": {
                    fontSize: theme.font_size.f20,
                    fontWeight: "700",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    transition: "all .2s",
                    "@media (max-width: 320px)": {
                        fontSize: theme.font_size.f16,
                    },
                },
                img: {
                    cursor: "pointer",
                    height: "1.5em",
                },
            }),
            MobileSecretNoteWrap: styled.div({
                margin: "2em 1em 1em 1em",
                padding: "1em 1.5em",
                backgroundColor: theme.colors.play_background,
                borderRadius: "10px",
                transition: "all .2s",
                "& > a": {
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                },
                "& > a > span": {
                    fontSize: theme.font_size.f17,
                    fontWeight: "500",
                    transition: "all .2s",
                    "@media (max-width: 320px)": {
                        fontSize: theme.font_size.f15,
                    },
                },
                "& > a > .download-icon": {
                    width: "2em",
                    height: "2em",
                    background: `url(${getImageUrl(
                        "image/course/ic_download_black.png"
                    )}) center center/contain no-repeat`,
                },
                "@media (max-width: 320px)": {
                    padding: ".5em 1em",
                },
            }),
        },
    };
};
